import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import RefInt from './lib/refint';
import { detect } from 'detect-browser';
import * as regex from '@zhortech/commons-digital-modules-regex';
import axios from 'axios';
import { getProcessEnv } from '@/lib/build';
import i18n from '@/plugins/i18n';

import pkg from '@/../package.json';
import { get } from 'lodash';
import { TAcquisition, TAcquisition_ComputedData } from '@/plugins/podolib/services/entities/EntityPatients';
import { AnalysisType } from '@/components/analysis/steps/type/AnalysisStepType';

// Service options type
export type TOptions = {};

interface EnumItem<E> {
  text: keyof E;
  value: E;
}

export type SetFrameInterval = {
  clear(): void;
};

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class HelperService extends AbstractService<TOptions, any> {
  private _browser: string | null = null;

  /**
   * @param options
   */
  constructor(options: TOptions) {
    super(options, 'helper');
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>) {
    await super.init(Vue, store);

    this.defineFilters();

    const b = detect();
    if (b) this._browser = b.name;
  }

  private defineFilters() {
    // Capitalization of the first letter
    this.Vue.filter('capitalize', function (value: any) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    });

    // Add stylized * after mandatory field label
    this.Vue.filter('mandatory', function (value: any) {
      if (!value) return '';
      value = value.toString();
      return `${value} *`;
    });

    // Truncate
    this.Vue.filter('truncate', function (value: any, limit: number) {
      if (value && value.length > limit) {
        value = value.substring(0, limit - 3) + '...';
      }
      return value;
    });
  }

  get isDev() {
    return process.env.NODE_ENV === 'development';
  }

  get isTest() {
    return process.env.NODE_ENV === 'test';
  }

  get isProd() {
    return process.env.NODE_ENV === 'production';
  }

  // Gateway
  get refint() {
    return RefInt;
  }

  // UNITS
  radToDeg(val: number) {
    return (val * 180) / Math.PI;
  }
  secToMs(val: number) {
    return val * 1000;
  }
  mpsToKmh(val: number) {
    return val * 3.6;
  }
  mToCm(val: number) {
    return val * 100;
  }
  //

  isAnExistingLanguage(lang: 'en' | 'fr' | 'de' | 'it') {
    if (['en', 'fr', 'de', 'it'].includes(lang)) {
      return lang;
    } else {
      return 'en';
    }
  }

  isNewAnalysis(acquisition: TAcquisition_ComputedData) {
    const values = acquisition.aggregates.left;
    return (
      values?.angle_flat_foot !== undefined &&
      values?.stride_height !== undefined &&
      values?.braking_phase !== undefined &&
      values?.angle_attack !== undefined &&
      values?.propulsive_phase !== undefined &&
      values?.percent_fore_foot !== undefined &&
      values?.percent_mid_foot !== undefined &&
      values?.percent_rear_foot !== undefined &&
      values?.f_max !== undefined
    );
  }

  get regex() {
    return regex;
  }

  get angleAverage(): any {
    return {
      toesOff: {
        min: -7.5,
        max: -2.5,
      },
      heelOff: {
        min: -12.5,
        max: -7.5,
      },
      toesStrike: {
        min: -12.5,
        max: -7.5,
      },
      heelStrike: {
        min: -17.5,
        max: -12.5,
      },
    };
  }
  get runningAngleAverage(): any {
    return {
      propulsion: {
        min: -6,
        max: -2,
      },
      flatFoot: {
        min: -15,
        max: -5,
      },
      strike: {
        min: -7,
        max: 2,
      },
    };
  }

  /**
   * Get interface current version
   * @deprecated
   */
  get version(): string {
    let main = pkg.version;
    const phase = process.env.VUE_APP_BRANCH || 'local';
    const hash = process.env.VUE_APP_HASH;

    if (phase !== 'production') {
      main += `.${phase}`;
    }
    if (hash) {
      main += `.${hash}`;
    }
    return main;
  }

  /**
   * Check if it's necessary to update interface
   * @deprecated
   */
  public async askToServerCurrentVersion(): Promise<string | null> {
    const response = await fetch(getProcessEnv('CLOUD_URL') + 'rest/versions?component=front');
    const result = await response.json();

    return result.version;
  }

  get dateFormatRelatedToSelectedLanguage() {
    switch (this.Vue.prototype.$pdc.doctor.current.config.lang) {
      case 'fr':
        return 'DD/MM/YYYY';
      default:
        return 'MM/DD/YYYY';
    }
  }

  get browser(): string | null {
    return this._browser;
  }

  get userAgent() {
    return navigator.userAgent;
  }

  get isKiosk() {
    return this.userAgent === 'kiosk';
  }

  get isDemo() {
    return !this.Vue.prototype.$pdc.entities!.stations.storedEntity.list.length;
  }

  get isCompatibleBrowser() {
    // Exclude the kiosk from verification
    if (this.isKiosk) return true;

    // CHeck browser
    switch (this.browser) {
      // case 'chrome':
      case 'firefox':
      case 'safari':
        // case 'edge-chromium':
        return true;
      default:
        return false;
    }
  }

  // TODO : move to env variable
  get cdn() {
    return 'https://s3.eu-central-1.amazonaws.com/cdn.podosmart.tech';
  }

  fromCdn(type: 'image' | 'docs' | 'other', filename: string, version?: string) {
    let url = '';
    switch (type) {
      case 'image':
        url += `${this.cdn}/medias/images/${filename}`;
        break;
      case 'docs':
        url += `${this.cdn}/medias/docs/${filename}`;
        break;
      case 'other':
        url += `${this.cdn}/${filename}`;
        break;
    }
    if (version) {
      url += `?versionId=${version}`;
    }
    return url;
  }

  getQrcodeAccess() {
    return axios.get(getProcessEnv('CLOUD_URL') + 'rest/qrcodeaccess', {
      headers: {
        authorization: `Bearer ${this.Vue.prototype.$pdc.cognito.accessToken}`,
      },
    });
  }

  get supportLink() {
    switch (this.Vue.prototype.$pdc.doctor!.current?.config.lang) {
      case 'fr':
        return 'https://www.podosmart.tech/fr/support-clients';
      default:
        return 'https://www.podosmart.tech/customer-support';
    }
  }

  enumToArray<E>(enumeration: E): EnumItem<E>[] {
    return Object.entries(enumeration).map((entry) => ({
      text: entry[0] as keyof E,
      value: (entry[1] as unknown) as E,
    }));
  }

  enumToArrayAndTranslate<E>(
    enumeration: E,
    { key, plural, payload }: { key: string; plural?: boolean; payload?: any },
  ) {
    const _enumArray = this.enumToArray(enumeration);

    return _enumArray.map((entry) => ({
      ...entry,
      text: plural ? i18n.tc(`${key}.${entry.value}`, payload) : i18n.t(`${key}.${entry.value}`, payload),
    }));
  }

  isValidActivity(activity: any | null) {
    if (activity) {
      if (activity.analysisType.toLowerCase() === AnalysisType.Running) {
        const validSegments = activity.computed_data.segments.filter(
          (x: any) =>
            x.left.result.aggregates?.contact_time?.avg !== 0 &&
            x.right.result.aggregates?.contact_time?.avg !== 0 &&
            activity.computed_data?.aggregates?.left?.percent_flight_time,
        );
        return !!validSegments.length;
      }
      return activity.computed_data.valid !== 0;
    }
    return false;
  }

  isDegradedActivity(activity: any | null) {
    if (activity) {
      if (activity.computed_data.valid === 1) return true;

      // Mandatory
      const stride_length = !!activity.computed_data.left.aggregates?.stride_length?.avg;
      const circumduction = !!activity.computed_data.left.aggregates?.circumduction?.avg;
      const step_by_step_cadence = !!activity.computed_data.left.aggregates?.step_by_step_cadence?.avg;
      const walking_speed = !!activity.computed_data.left.aggregates?.walking_speed?.avg;
      const foot_progression_angle_FF = !!activity.computed_data.left.aggregates?.foot_progression_angle_FF?.avg;
      // Null
      const stance_time = !!activity.computed_data.left.aggregates?.stance_time?.avg;
      const swing_time = !!activity.computed_data.left.aggregates?.swing_time?.avg;
      const flatfoot_time = !!activity.computed_data.left.aggregates?.flatfoot_time?.avg;
      const propulsion_time = !!activity.computed_data.left.aggregates?.propulsion_time?.avg;
      const loading_time = !!activity.computed_data.left.aggregates?.loading_time?.avg;
      const pronation_angle_FFI = !!activity.computed_data.left.aggregates?.pronation_angle_FFI?.avg;
      const plantar_flexion_angle_foot_in = !!activity.computed_data.left.aggregates?.plantar_flexion_angle_foot_in
        ?.avg;
      const plantar_flexion_angle_foot_out = !!activity.computed_data.left.aggregates?.plantar_flexion_angle_foot_out
        ?.avg;
      const pronation_angle_FFO = !!activity.computed_data.left.aggregates?.pronation_angle_FFO?.avg;

      return (
        stride_length &&
        circumduction &&
        step_by_step_cadence &&
        walking_speed &&
        foot_progression_angle_FF &&
        !stance_time &&
        !swing_time &&
        !flatfoot_time &&
        !propulsion_time &&
        !loading_time &&
        !pronation_angle_FFI &&
        !plantar_flexion_angle_foot_in &&
        !plantar_flexion_angle_foot_out &&
        !pronation_angle_FFO
      );
    }
    return false;
  }

  isGoodActivity(activity: any | null) {
    if (activity) {
      if (activity.analysisType.toLowerCase() === AnalysisType.Running) {
        const validSegments = activity.computed_data.segments.filter(
          (x: any) =>
            x.left.aggregates?.stance_time?.avg === 0 &&
            x.right.aggregates?.stance_time?.avg === 0 &&
            activity.computed_data?.aggregates?.left?.percent_flight_time,
        );
        return !!validSegments.length;
      }
      // return activity.computed_data.valid === 2;
      return true;
    }
    return false;
  }

  public setFrameInterval(callback: Function, delay: number = 0): SetFrameInterval {
    const dateNow = Date.now;
    const requestAnimation = window.requestAnimationFrame;
    let start = dateNow();
    let stop = 0;
    const intervalFunc = () => {
      dateNow() - start < delay || ((start += delay), callback());
      stop || requestAnimation(intervalFunc);
    };
    requestAnimation(intervalFunc);
    return {
      clear() {
        stop = 1;
      },
    };
  }

  public pixelize(value: string | number) {
    return value + 'px';
  }

  public downloadPdf(url: string, fileName: string) {
    axios({
      url: url,
      method: 'GET',
      responseType: 'blob',
    })
      .then((response) => {
        let fileURL = window.URL.createObjectURL(new Blob([response.data]));
        let fileLink = document.createElement('a');

        fileLink.href = fileURL;
        fileLink.setAttribute('download', `${fileName}.pdf`);
        document.body.appendChild(fileLink);

        fileLink.click();
      })
      .catch((e) => {
        console.log(e);
      });
  }
}
